/**
 * Component: Service plan form
 * ------------------------------------------------------------------------------
 * Form functionality for service plans page.
 *
 * @namespace spForm
 */
import log from 'salvo-lite/log'
import axios from 'axios'
import {formatMoney} from '@shopify/theme-currency'
import debounce from 'lodash-es/debounce'
import * as cart from '@shopify/theme-cart'
import XMLParser from '../vendors/fastXmlParser'
import breakpoints from '../helpers/breakpoints'
import cssClasses from '../helpers/cssClasses'
import zenscroll from 'zenscroll'
import {on} from '../helpers/utils'

import { garageService, garageServiceReady } from '../services/garageService'
class ServicePlansForm {
    constructor (theme, elem) {
        this._elem = elem
        this._vinForm = '[js-sp-form="vin-form"]'
        this._settings = {
            container: '[js-sp-form="container"]',
            // All steps
            monthlyCost: '[js-sp-form="monthlyCost"]',
            monthlyPayment: '[js-monthly-payment="element"]',
            totalCost: '[js-sp-form="totalCost"]',
            validity: '[js-sp-form="validity"]',
            stepComplete: 'sp-form-step--complete',
            stepToggle: '[js-sp-form-toggle]',
            // Step 1
            step1: '[js-sp-form="step1"]',
            annualMileage: '[js-sp-form="annualMileage"]',
            annualMileageError: '[js-sp-form="annualMileageError"]',
            slider: '[js-sp-form="slider"]',
            sliderValue: '[js-sp-form="sliderValue"]',
            duration: '[js-sp-form="duration"]',
            confirmation: '[js-sp-form="confirmation"]',
            confirmation2: '[js-sp-form="confirmation2"]',
            confirmation3: '[js-sp-form="confirmation3"]',
            confirmationcontainer: '[js-sp-form="confirmationcontainer"]',
            confirmation2container: '[js-sp-form="confirmation2container"]',
            confirmation3container: '[js-sp-form="confirmation3container"]',
            status: '[js-sp-form="status"]',
            statusCopy: '[js-sp-form="statusCopy"]',
            costs: '[js-sp-form="costs"]',
            wearAndTear: '[js-sp-form="wearAndTear"]',
            servicePlan: '[js-sp-form="servicePlan"]',
            serviceBundle: '[js-sp-form="serviceBundle"]',
            // Step 2
            step2: '[js-sp-form="step2"]',
            step2Save: '[js-sp-form="step2Save"]',
            field: '[js-sp-form="field"]',
            // Step 3
            step3: '[js-sp-form="step3"]',
            planType: '[js-sp-form="planType"]',
            years: '[js-sp-form="years"]',
            distance: '[js-sp-form="annualMileage"]',
            purchaseDate: '[js-sp-form="purchaseDate"]',
            addToCart: '[js-sp-form="addToCart"]',
            submitForm: '[js-sp-form="submitForm"]',
            dealerInfo: '[js-sp-form="dealerInfo"]',
            dealerLink: '[js-sp-form="dealerLink"]',
            dealerEmail: '[js-sp-form="dealerEmail"]',
            completeMessage: '[js-sp-form="completeMessage"]',
            ...JSON.parse(elem.getAttribute('data-service-plans-form') || '{}')
        }

        /**
         * DOM node selectors.
         */
        const step1 = document.querySelector(this._settings.step1)
        const step2 = document.querySelector(this._settings.step2)
        const step3 = document.querySelector(this._settings.step3)

        this._nodeSelectors = {
            container: document.querySelector(this._settings.container),
            vinForm: document.querySelector(this._vinForm),
            // All steps
            step1: this._elem.querySelector(this._settings.step1),
            step2: this._elem.querySelector(this._settings.step2),
            step3: this._elem.querySelector(this._settings.step3),
            monthlyCost: [...document.querySelectorAll(this._settings.monthlyCost)],
            monthlyPayment: [...document.querySelectorAll(this._settings.monthlyPayment)],
            totalCost: [...document.querySelectorAll(this._settings.totalCost)],
            stepToggle: this._elem.querySelectorAll(this._settings.stepToggle),
            // Step 1
            annualMileage: step1.querySelector(this._settings.annualMileage),
            annualMileageError: step1.querySelector(this._settings.annualMileageError),
            slider: step1.querySelector(this._settings.slider),
            sliderValue: step1.querySelector(this._settings.sliderValue),
            duration: [...step1.querySelectorAll(this._settings.duration)],
            confirmation: step1.querySelector(this._settings.confirmation),
            confirmation2: step1.querySelector(this._settings.confirmation2),
            confirmation3: step1.querySelector(this._settings.confirmation3),
            confirmationcontainer: step1.querySelector(this._settings.confirmationcontainer),
            confirmation2container: step1.querySelector(this._settings.confirmation2container),
            confirmation3container: step1.querySelector(this._settings.confirmation3container),
            status: document.querySelector(this._settings.status),
            statusCopy: document.querySelector(this._settings.statusCopy),
            costs: this._elem.querySelector(this._settings.costs),
            wearAndTear: step1.querySelector(this._settings.wearAndTear),
            servicePlan: step1.querySelector(this._settings.servicePlan),
            serviceBundle: step1.querySelector(this._settings.serviceBundle),
            // Step 2
            step2Save: step2.querySelector(this._settings.step2Save),
            field: [...step2.querySelectorAll(this._settings.field)],
            // Step 3
            planType: step3.querySelector(this._settings.planType),
            years: step3.querySelector(this._settings.years),
            distance: step3.querySelector(this._settings.distance),
            purchaseDate: step3.querySelector(this._settings.purchaseDate),
            addToCart: document.querySelector(this._settings.addToCart),
            dealerInfo: document.querySelector(this._settings.dealerInfo),
            dealerLink: document.querySelector(this._settings.dealerLink),
            dealerEmail: document.querySelector(this._settings.dealerEmail),
            submitForm: document.querySelector(this._settings.submitForm),
            completeMessage: document.querySelector(this._settings.completeMessage)
        }

        /**
         * Global data object.
         */
        this._planData = {}
        this._annualMileage = false
        this._duration = false
        this._sliderEnabled = false
        this._locale = 'en-GB'
        this._annualMileageErrorState = false
        this._wearAndTear = false
        this._servicePlan = false
        this._serviceBundle = false

        if (window.theme.store.id === 'de_store') {
            this._locale = 'de-DE'
        }

        /**
         * Event used to automatically update annual mileage value.
         */
        this._event = document.createEvent('HTMLEvents')
        this._event.initEvent('keyup', false, true)

        log.debug(this.constructor.name, 'Constructed', this)
    }

    /**
     * Initialise component.
     */
    onInit () {
        this._setListeners()
        this._setEventListeners()
        this._setComponentStates()
        this._setDefaultState()
        this._sliderCheck()
        this._listenForGarageUpdates()
        this._checkCurrentVehicle()
    }

    async _listenForGarageUpdates () {
        await garageServiceReady

        garageService.onActiveVehicleChange.subscribe(async (v) => {
            await this._checkCurrentVehicle()

            if (v === null) {
                document.body.classList.remove('current-car-vin-set')
                document.body.classList.remove('current-car-set')
                this._resetFormSubmission()
            }
        })
    }

    async _checkCurrentVehicle () {
        if (window._esc_GarageService._activeVehicle === null || !('vin' in garageService.activeVehicle)) return

        const reg = window._esc_GarageService._activeVehicle.vin.replace(/\s/g, '')

        const data = await this._getVehicleData(reg)

        this._updateData('emissionCode', data.data.result.emissionCode)
        this._updateData('transmissionCode', data.data.result.transmissionCode)
        this._updateData('engineCode', data.data.result.engineCode.substring(2, 4))
        this._updateData('productionDate', data.data.result.productionDate)
        this._updateData('vehicleCode', data.data.result.vehicleCode)
        this._updateData('vinNumber', data.data.result.vin)
        this._updateData('year', window._esc_GarageService._activeVehicle.year.toString())

        document.body.classList.add('current-car-vin-set')
        document.body.classList.add('current-car-set')
    }

    /**
   * Check component functionality.
   */
    _setComponentStates () {
        if (this._nodeSelectors.annualMileage) {
            this._annualMileage = true
        }

        this._duration = true
    }

    /**
     * Set default form state.
     */
    _setDefaultState () {
        this._planData = {}

        if (this._annualMileage) {
            if (this._sliderEnabled) {
                this._nodeSelectors.slider.value = Number(this._nodeSelectors.slider.getAttribute('data-default'))
                this._handleSliderUpdate()
                this._handleSliderMileageUpdate()
            } else {
                this._updateData('annualMileage', 0)
                this._nodeSelectors.annualMileage.value = ''
                this._updateFieldValidity(this._nodeSelectors.annualMileage, false)
            }
        }

        if (this._duration) {
            this._nodeSelectors.duration.forEach((element, index) => {
                if (index === 0) {
                    element.checked = true
                    this._updateData('duration', Number(element.value))
                    return
                }

                element.checked = false
            })
        }

        if (this._annualMileage) {
            this._nodeSelectors.confirmation.removeAttribute('disabled')
            if (this._nodeSelectors.confirmation2) {
                this._nodeSelectors.confirmation2.removeAttribute('disabled')
            }
            if (this._nodeSelectors.confirmation3) {
                this._nodeSelectors.confirmation3.removeAttribute('disabled')
            }
        }
    }

    /**
     * Get vehicle data from API
     * @param {string} searchValue
     */
    async _getVehicleData (searchValue) {
        let searchUrl = `${window._esc_PartIndexer_config.api.apiBaseUrl}/vehicle/details?vin=${searchValue}&store=${window._esc_PartIndexer_config.context.store}`

        return new Promise((resolve, reject) => {
            axios.get(searchUrl)
                .then((response) => resolve(response))
                .catch((error) => reject(error))
        })
    }

    /**
     * Set trigger listeners.
     */
    _setListeners () {
        on('resize', debounce(this._sliderCheck, 250))

        on('keyup', this._nodeSelectors.annualMileage, () => this._handleAnnualMileageChange())

        this._nodeSelectors.duration.forEach((element) => {
            on('change', element, () => this._handleDurationChange(element))
        })

        on('change', this._nodeSelectors.confirmation, () => this._handleConfirmationChange())
        if (this._nodeSelectors.confirmation2) {
            on('change', this._nodeSelectors.confirmation2, () => this._handleConfirmationChange())
        }
        if (this._nodeSelectors.confirmation3) {
            on('change', this._nodeSelectors.confirmation3, () => this._handleConfirmationChange())
        }

        // Wear and Tear plan click action
        if (this._nodeSelectors.wearAndTear) {
            on('click', this._nodeSelectors.wearAndTear, () => {
                this._handlePlanTypeChange('wearAndTear')
                if (this._nodeSelectors.confirmationcontainer.classList.contains('is-hidden') === true) {
                    this._nodeSelectors.confirmationcontainer.classList.remove('is-hidden')
                    this._nodeSelectors.confirmation2container.classList.add('is-hidden')
                    this._nodeSelectors.confirmation3container.classList.add('is-hidden')
                }
            })
        }

        // Service plan click action
        if (this._nodeSelectors.servicePlan) {
            on('click', this._nodeSelectors.servicePlan, () => {
                this._handlePlanTypeChange('servicePlan')
                if (this._nodeSelectors.confirmation2container.classList.contains('is-hidden') === true) {
                    this._nodeSelectors.confirmation2container.classList.remove('is-hidden')
                    this._nodeSelectors.confirmationcontainer.classList.add('is-hidden')
                    this._nodeSelectors.confirmation3container.classList.add('is-hidden')
                }
            })
        }

        // combined plan click action
        if (this._nodeSelectors.serviceBundle) {
            on('click', this._nodeSelectors.serviceBundle, () => {
                this._handlePlanTypeChange('serviceBundle')
                if (this._nodeSelectors.confirmation3container.classList.contains('is-hidden') === true) {
                    this._nodeSelectors.confirmation3container.classList.remove('is-hidden')
                    this._nodeSelectors.confirmationcontainer.classList.add('is-hidden')
                    this._nodeSelectors.confirmation2container.classList.add('is-hidden')
                }
            })
        }

        this._nodeSelectors.field.forEach((element) => {
            on('keyup', element, () => this._handleFieldChange(element))
        })

        on('click', this._nodeSelectors.step2Save, () => this._handleStep2Save())

        if (this._nodeSelectors.addToCart) {
            on('click', this._nodeSelectors.addToCart, () => this._handleAddToCart())
        }

        if (this._nodeSelectors.submitForm) {
            on('click', this._nodeSelectors.submitForm, () => this._handleSubmitForm())
        }

        on('click', this._elem.querySelector('[js-sp-continue="step3"]'), () => {
            if (!this._nodeSelectors.step3.classList.contains(this._settings.stepComplete)) {
                this._nodeSelectors.step3.classList.add(this._settings.stepComplete)
                this._scrollTo('.sp-form-step--3')
            }

            this._nodeSelectors.container.setAttribute('data-form-step', 'step2')
        })

        // stepToggle
        this._nodeSelectors.stepToggle.forEach((elem) => on('click', elem, (e) => this._toggleStep(e.target.getAttribute('js-sp-form-toggle'))))

        if (this._nodeSelectors.dealerLink) {
            on('click', this._nodeSelectors.dealerLink, (e) => this._handleDealerLink(e))
            on('keyup', this._nodeSelectors.dealerEmail, (e) => this._handleDealerEmailInput(e))
        }
    }

    /**
     * Set eventBus listeners.
     */
    _setEventListeners () {
        window.Frame.EventBus.listen('AjaxCart:itemAdded', () => this._handleItemAdded())
        window.Frame.EventBus.listen('ServicePlansForm:reset', () => this._resetForm())
    }

    /**
     * Toggle between steps
     */
    _toggleStep (step) {
        this._nodeSelectors.container.setAttribute('data-form-step', step)

        if (step === 'step1') {
            this._nodeSelectors.confirmation.checked = false
            if (this._nodeSelectors.confirmation2) {
                this._nodeSelectors.confirmation2.checked = false
            }
            if (this._nodeSelectors.confirmation3) {
                this._nodeSelectors.confirmation3.checked = false
            }
        }
    }

    /**
     * Handle resize event and check whether to init slider.
     */
    _sliderCheck () {
        if (!this._annualMileage) {
            return
        }

        if (window.matchMedia(`(min-width: ${breakpoints.large})`).matches) {
            this._sliderInit()
            return
        }

        this._sliderEnabled = false
    }

    /**
     * Handle wear and tear checkbox change.
     */
    _handlePlanTypeChange (planType) {
        this._wearAndTear = this._nodeSelectors.wearAndTear ? this._nodeSelectors.wearAndTear.checked : false
        this._servicePlan = this._nodeSelectors.servicePlan ? this._nodeSelectors.servicePlan.checked : false
        this._serviceBundle = this._nodeSelectors.serviceBundle ? this._nodeSelectors.serviceBundle.checked : false

        this._updateData('wearAndTear', (this._wearAndTear === true ? 'Yes' : 'No'))
        this._updateData('servicePlan', (this._servicePlan === true ? 'Yes' : 'No'))
        this._updateData('serviceBundle', (this._serviceBundle === true ? 'Yes' : 'No'))
    }

    /**
     * Controls whether to show the dealer e-mail input box, and what information gets sent to BAAC
     */
    _handleDealerLink (e) {
        const input = e.target
        this._updateData('dealerLink', input.checked)

        const dealerEmail = this._nodeSelectors.dealerEmail
        if (input.checked === true) {
            dealerEmail.classList.remove('is-hidden')
        } else {
            dealerEmail.classList.add('is-hidden')
        }
        this._validateBAACSubmission()
    }
    _handleDealerEmailInput (e) {
        const input = e.target
        this._updateData('dealerEmail', input.value)
        this._validateBAACSubmission()
    }

    /**
     * Initialise slider.
     */
    _sliderInit () {
        this._sliderEnabled = true
        /**
         * Set value of mileage input.
         */
        this._nodeSelectors.annualMileage.value = this._nodeSelectors.slider.value
        this._nodeSelectors.annualMileage.dispatchEvent(this._event)
        this._updateData('annualMileage', this._nodeSelectors.annualMileage.value)

        /**
         * Set triggers.
         */
        on('input', this._nodeSelectors.slider, () => this._handleSliderUpdate())
        on('change', this._nodeSelectors.slider, () => this._handleSliderUpdate())
        on('touchmove', this._nodeSelectors.slider, () => this._handleSliderUpdate())
        on('mouseup', this._nodeSelectors.slider, () => this._handleSliderMileageUpdate())
    }

    /**
     * Handle changes.
     * ------------------------------------------------------------------------------
     * Actions form change when form is interacted with.
     *
     */

    /**
     * Handle annual mileage change.
     */
    _handleAnnualMileageChange () {
        clearTimeout(this._setTimeout)
        this._nodeSelectors.annualMileageError.classList.remove(cssClasses.active)
        this._nodeSelectors.annualMileageError.innerText = ''

        const value = Number(this._nodeSelectors.annualMileage.value)
        this._updateData('annualMileage', value)

        if (this._isFormSubmitted()) {
            this._resetFormSubmission()
        }

        if (this._isAnnualMileageValid()) {
            this._nodeSelectors.confirmation.removeAttribute('disabled')
            this._nodeSelectors.slider.value = value
            this._nodeSelectors.sliderValue.innerText = value.toLocaleString(this._locale)
            this._updateFieldValidity(this._nodeSelectors.annualMileage, true)
            return
        }

        this._nodeSelectors.confirmation.setAttribute('disabled', true)
        this._updateFieldValidity(this._nodeSelectors.annualMileage, false)

        /**
         * Show min/max errors.
         */

        /**
         * Show error after 1s if the value is too high or low
         */
        this._setTimeout = setTimeout(() => {
            const min = Number(this._nodeSelectors.annualMileage.getAttribute('min'))
            const max = Number(this._nodeSelectors.annualMileage.getAttribute('max'))
            const allowedMax = Number(this._nodeSelectors.annualMileage.getAttribute('allowedmax'))

            let errorString = ''

            if (value < min) {
                errorString = `${min.toLocaleString(this._locale)} ${window.theme.strings.standard.miles} ${window.theme.strings.standard.minimum}`
            } else if (value > max) {
                errorString = `${max.toLocaleString(this._locale)} ${window.theme.strings.standard.miles} ${window.theme.strings.standard.maximum}`
            } else if (value > allowedMax) {
                errorString = `${allowedMax.toLocaleString(this._locale)} ${window.theme.strings.standard.miles} ${window.theme.strings.standard.maximum}`
            }

            this._nodeSelectors.annualMileageError.innerText = errorString
            this._nodeSelectors.annualMileageError.classList.add(cssClasses.active)
        }, 1000)
    }

    /**
     * Handle contract duration change.
     * @param {HTMLElement} element - The selected element.
     */
    _handleDurationChange (element) {
        this._updateData('duration', Number(element.value))

        if (this._isFormSubmitted()) {
            this._resetFormSubmission()
        }

        if (this._isAnnualMileageValid()) {
            this._nodeSelectors.confirmation.removeAttribute('disabled')
        } else {
            this._nodeSelectors.confirmation.setAttribute('disabled', true)
        }
    }
    _getHeaderHeight () {
        return window.__Theme.getSectionsByType('site-header')._currentHeight
    }
    _scrollTo (elem) {
        let targetElement = document.querySelector(elem)
        let top = targetElement.getBoundingClientRect().top + zenscroll.getY() - this._getHeaderHeight()
        zenscroll.toY(top, 999)
    }
    /**
     * Handle confirmation checkbox change.
     */
    _handleConfirmationChange () {
        this._handlePlanTypeChange()

        if ((this._nodeSelectors.confirmation && this._nodeSelectors.confirmation.checked) ||
            (this._nodeSelectors.confirmation2 && this._nodeSelectors.confirmation2.checked) ||
            (this._nodeSelectors.confirmation3 && this._nodeSelectors.confirmation3.checked) || (!this._nodeSelectors.confirmation2)) {
            this._nodeSelectors.status.classList.remove(cssClasses.active)

            if (this._isAnnualMileageValid()) {
                this._submitSearch()
                if (!this._nodeSelectors.step1.classList.contains(this._settings.stepComplete)) {
                    this._nodeSelectors.step1.classList.add(this._settings.stepComplete)
                    this._scrollTo('.sp-form-step--1')
                }
            } else {
                this._handleError('Invalid mileage')
            }
            return
        }

        if (this._isFormSubmitted()) {
            this._resetFormSubmission()
        }
    }

    /**
     * Handles step 2 field changes.
     * @param {HTMLElement} element - The changed element.
     */
    _handleFieldChange (element) {
        const key = element.getAttribute('data-input')
        if (element.value === '') {
            this._updateFieldValidity(element, false)
        } else {
            if (key === 'zip') {
                this._updateFieldValidity(element, this._isZipValid(element))
            } else {
                this._updateFieldValidity(element, true)
            }
        }

        if (this._isStep2Valid()) {
            this._nodeSelectors.step2Save.removeAttribute('disabled')
        } else {
            this._nodeSelectors.step2Save.setAttribute('disabled', true)
        }
    }

    /**
     * Handle the step 2 address being saved/updated.
     */
    _handleStep2Save () {
        if (!this._isStep2Valid()) {
            return
        }

        if (this._nodeSelectors.step2Save.getAttribute('data-state') === 'update') {
            this._nodeSelectors.step2Save.innerText = window.theme.strings.servicePlans.step2.updating
            this._nodeSelectors.step2Save.setAttribute('disabled', true)

            window.setTimeout(() => {
                this._nodeSelectors.step2Save.innerText = window.theme.strings.servicePlans.step2.update
                this._nodeSelectors.step2Save.removeAttribute('disabled')
            }, 2000)
        } else {
            this._nodeSelectors.step2Save.innerText = window.theme.strings.servicePlans.step2.update
        }

        this._nodeSelectors.field.forEach((element) => {
            const key = element.getAttribute('data-input')
            let value = element.value

            if (key === 'currentMileage') {
                value = Number(element.value)
            }

            this._updateData(key, value)
        })

        this._nodeSelectors.container.setAttribute('data-form-step', 'complete')

        if (!this._nodeSelectors.step2.classList.contains(this._settings.stepComplete)) {
            this._nodeSelectors.step2.classList.add(this._settings.stepComplete)
        }

        this._nodeSelectors.step2Save.setAttribute('data-state', 'update')

        if (this._nodeSelectors.addToCart) {
            this._nodeSelectors.addToCart.removeAttribute('disabled')
        }

        if (this._nodeSelectors.submitForm) {
            if (this._nodeSelectors.dealerLink) {
                this._nodeSelectors.dealerInfo.classList.remove('is-hidden')
                this._validateBAACSubmission()
            } else {
                this._nodeSelectors.submitForm.removeAttribute('disabled')
            }
        }

        if (this._nodeSelectors.completeMessage) {
            this._nodeSelectors.completeMessage.classList.remove(cssClasses.hidden)
        }
        this._scrollTo('.sp-form__container')
    }
    /**
     * Checks whether dealer information is valid for BAAC Request
     */
    _validateBAACSubmission () {
        if (
            this._nodeSelectors.dealerLink.checked &&
            (!this._nodeSelectors.dealerEmail.validity.valid || this._nodeSelectors.dealerEmail.value === '')
        ) {
            this._nodeSelectors.submitForm.setAttribute('disabled', true)
        } else {
            this._nodeSelectors.submitForm.removeAttribute('disabled')
        }
    }

    /**
     * Handle add to cart.
     */
    _handleAddToCart () {
        if (this._nodeSelectors.addToCart) {
            this._nodeSelectors.addToCart.setAttribute('disabled', true)
        }

        if (this._nodeSelectors.submitForm) {
            this._nodeSelectors.submitForm.setAttribute('disabled', true)
        }

        let name = `${this._planData.firstName} ${this._planData.lastName}`
        let address = `${this._planData.address1}, ${this._planData.city}, ${this._planData.zip}, ${this._planData.country}`

        if (this._planData.middleInitial !== '') {
            name = `${this._planData.firstName} ${this._planData.middleInitial} ${this._planData.lastName}`
        }

        if (this._planData.address2 !== '') {
            address = `${this._planData.address1}, ${this._planData.address2}, ${this._planData.city}, ${this._planData.zip}, ${this._planData.country}`
        }

        const id = Number(this._nodeSelectors.addToCart.getAttribute('data-id'))
        const options = {
            quantity: 1,
            properties: {
                name,
                address,
                currentMileage: this._planData.current,
                distance: this._planData.distance,
                vehicleCode: this._planData.vehicleCode,
                engineCode: this._planData.engineCode,
                emissionCode: this._planData.emissionCode,
                vinNumber: this._planData.vinNumber
                // wearAndTear: this._planData.wearAndTear
                // todo update here
            }
        }

        if (this._duration) {
            options.properties.duration = this._planData.duration
        }

        const klarnaPrice = this._elem.querySelector(this._settings.monthlyCost).innerText
        const singlePaymentPrice = this._elem.querySelector(this._settings.totalCost).innerText

        // Push price to data layer
        window.dataLayer.push({
            event: 'ServicePlanCalculator',
            category: 'Calculator: ServicePlan',
            action: 'Klarna/Single Payment',
            label: `${klarnaPrice}/${singlePaymentPrice}`
        })

        // Push the following keys to the data later
        const dataLayerItems = ['annualMileage', 'duration']
        dataLayerItems.forEach((key) => {
            window.dataLayer.push({
                category: 'Calculator: ServicePlan',
                action: key,
                label: this._planData[key]
            })
        })

        cart.addItem(id, options)
            .then((response) => {
                window.location.replace(window.location.origin + '/cart')
            }).catch((error) => error)
    }

    /**
     * Handle dealer form submission
     */
    _handleSubmitForm () {
        window.Frame.EventBus.emit('ServicePlanDealer:submit', this._planData)
        this._scrollTo('#shopify-section-service-plans-dealer-template')
    }

    /**
     * Handle item added successfully.
     */
    _handleItemAdded () {
        if (this._nodeSelectors.addToCart) {
            this._nodeSelectors.addToCart.innerText = window.theme.strings.servicePlans.checkout
        }

        window.setTimeout(() => {
            this._resetForm()

            window.scroll({
                behavior: 'smooth',
                left: 0,
                top: 0
            })
        }, 3000)
    }

    /**
     * Update slider value instantly.
     */
    _handleSliderUpdate () {
        const value = Number(this._nodeSelectors.slider.value)
        const displayValue = value.toLocaleString(this._locale)

        this._nodeSelectors.sliderValue.innerText = displayValue
    }

    /**
     * Update annual mileage on release to reduce overhead.
     */
    _handleSliderMileageUpdate () {
        this._nodeSelectors.annualMileage.value = this._nodeSelectors.slider.value
        this._nodeSelectors.annualMileage.dispatchEvent(this._event)
        this._updateData('annualMileage', this._nodeSelectors.annualMileage.value)
        this._resetFormSubmission()
    }

    /**
     * Global functions.
     * ------------------------------------------------------------------------------
     * Global functions to action form events.
     *
     */

    /**
     * Update global data.
     * @param {String} key - The key to update.
     * @param {String} value - The value to update the key with.
     */
    _updateData (key, value) {
        this._planData[key] = value

        switch (key) {
        case 'annualMileage':
            this._planData.distance = `${value.toLocaleString(this._locale)} ${window.theme.strings.standard.miles}`
            break

        case 'currentMileage':
            this._planData.current = `${value.toLocaleString(this._locale)} ${window.theme.strings.standard.miles}`
            break

        case 'duration':
            this._planData.years = `${value} ${window.theme.strings.standard.years}`
            break
        }
    }

    /**
     * Render step 3.
     * @param {Object} product - The service plan product.
     */
    _renderStep3 (product) {
        if (this._serviceBundle) {
            this._nodeSelectors.planType.innerHTML = `${window.theme.strings.servicePlans.step3.planType}: ${window.theme.strings.servicePlansDealer.planName.serviceBundle}`
        } else if (this._servicePlan) {
            this._nodeSelectors.planType.innerHTML = `${window.theme.strings.servicePlans.step3.planType}: ${window.theme.strings.servicePlansDealer.planName.servicePlan}`
        } else if (this._wearAndTear) {
            this._nodeSelectors.planType.innerHTML = `${window.theme.strings.servicePlans.step3.planType}: ${window.theme.strings.servicePlansDealer.planName.wearAndTear}`
        } else {
            this._nodeSelectors.planType.innerHTML = product.description
        }

        if (this._annualMileage !== false) {
            this._nodeSelectors.distance.innerText = this._planData.distance
        }

        if (this._duration) {
            this._nodeSelectors.years.innerText = this._planData.years
        }
    }

    /**
     * Update the add to cart button with product variant ID.
     * @param {Object} product - The service plan product.
     */
    _updateAddToCart (product) {
        this._nodeSelectors.addToCart.setAttribute('data-id', product.variants[0].id)
    }

    /**
     * Resets the entire form.
     */
    _resetForm () {
        this._setDefaultState()
        this._resetFormSubmission()
    }

    /**
     * Resets everything after annual mileage and contract duration.
     */
    _resetFormSubmission () {
        this._nodeSelectors.container.setAttribute('data-form-step', 'step0')
        this._nodeSelectors.container.setAttribute('data-form-submitted', 'false')
        this._nodeSelectors.status.classList.remove(cssClasses.active)
        this._elem.classList.remove('no-plans')

        /**
         * Step 1.
         */
        this._nodeSelectors.confirmation.checked = false
        if (this._nodeSelectors.confirmation2) this._nodeSelectors.confirmation2.checked = false
        if (this._nodeSelectors.confirmation3) this._nodeSelectors.confirmation3.checked = false

        if (this._annualMileage || this._duration) {
            if (!this._isAnnualMileageValid() && !this._sliderEnabled) {
                this._nodeSelectors.confirmation.setAttribute('disabled', true)
            }
        }

        // this._nodeSelectors.status.classList.remove(cssClasses.hidden)
        // this._nodeSelectors.status.classList.remove(cssClasses.loading)
        // this._nodeSelectors.statusCopy.innerText = window.theme.strings.servicePlans.step1.waiting
        this._nodeSelectors.costs.classList.remove(cssClasses.active)
        this._nodeSelectors.monthlyCost.innerText = formatMoney(0, window.theme.moneyFormat)
        this._nodeSelectors.totalCost.innerText = formatMoney(0, window.theme.moneyFormat)

        if (this._nodeSelectors.step1.classList.contains(this._settings.stepComplete)) {
            this._nodeSelectors.step1.classList.remove(this._settings.stepComplete)
        }

        /**
         * Step 2.
         */
        this._nodeSelectors.field.forEach((element) => {
            if (element.getAttribute('data-input') === 'country') {
                return
            }

            element.value = ''
            this._updateFieldValidity(element, false)
        })

        if (this._nodeSelectors.step3.classList.contains(this._settings.stepComplete)) {
            this._nodeSelectors.step3.classList.remove(this._settings.stepComplete)
        }

        this._nodeSelectors.step2Save.setAttribute('disabled', true)
        this._nodeSelectors.step2Save.innerText = window.theme.strings.servicePlans.step2.continue

        /**
         * Step 3.
         */
        this._nodeSelectors.planType.innerText = ''

        if (this._nodeSelectors.addToCart) {
            this._nodeSelectors.addToCart.innerText = window.theme.strings.servicePlans.checkout
        }

        if (this._nodeSelectors.step2.classList.contains(this._settings.stepComplete)) {
            this._nodeSelectors.step2.classList.remove(this._settings.stepComplete)
        }

        if (this._annualMileage) {
            this._nodeSelectors.distance.innerText = ''
        }

        if (this._duration) {
            this._nodeSelectors.years.innerText = ''
        }

        /**
         * Reset cost
         */
        let currency = ''

        switch (window.Shopify.currency.active) {
        case 'GBP':
            currency = '£'
            break

        case 'EUR':
            currency = '€'
            break

        default:
            currency = '£'
        }

        this._nodeSelectors.totalCost.forEach((element) => {
            element.innerHTML = `${currency} _ _ _`
        })

        this._nodeSelectors.monthlyCost.forEach((element) => {
            element.innerHTML = `${currency} _ _ _`
        })
    }

    /**
     * Form functions.
     * ------------------------------------------------------------------------------
     * Functions responsible for retrieving the data.
     *
     */

    /**
     * Submit search for service plans.
     */
    _submitSearch () {
        this._nodeSelectors.container.setAttribute('data-form-submitted', true)

        if (this._settings.dealer && this._settings.dealer === true) {
            let searchValue = 'product_type:ESB'

            // mandatory tags
            if (this._duration) {
                searchValue += `+tag:Duration-${this._planData.duration}`
            }
            const engineCode = `+tag:Engine_Code-${this._planData.engineCode}`
            const vehicleCode = `+tag:Vehicle_Code-${this._planData.vehicleCode}`

            searchValue += engineCode
            searchValue += vehicleCode

            let tag = ''

            // searchValue looks for a specific tag based on the users selection
            if (this._serviceBundle === true) {
                tag = '+tag:Plan_Type-ServicePlan-Wearandtear-Monthly-Package'
                searchValue += tag
            } else {
                if (this._wearAndTear === true) {
                    tag = `+tag:Plan_Type-Wearandtear-Monthly-Standalone`
                } else if (this._servicePlan === true) {
                    tag = `+tag:Plan_Type-ServicePlan-Monthly-Standalone`
                }
                searchValue += tag
            }

            this._updateData('planTag', tag.split('+tag:').pop())

            this._retrieveServicePlans(searchValue)
                .then((response) => {
                    this._searchForServicePlan(searchValue)

                    return response
                })
                .catch((error) => {
                    throw new Error('Could not load data', error)
                })
        } else {
            let searchValue = `product_type:ESB+tag:Engine_Code-${this._planData.engineCode}+tag:Vehicle_Code-${this._planData.vehicleCode}+tag:Emission_Code-${this._planData.emissionCode}`
            const transmissionValue = `product_type:ESB+tag:Transmission_Code-${this._planData.transmissionCode}`

            if (this._duration) {
                searchValue += `+tag:Duration-${this._planData.duration}`
            }

            let tag = ''

            // searchValue looks for a specific tag based on the users selection
            if (this._wearAndTear === true) {
                tag = `+tag:Plan_Type-Wearandtear`
            } else if (this._servicePlan === true) {
                tag = `+tag:Plan_Type-ServicePlan`
            }
            searchValue += tag

            this._updateData('planTag', tag.split('+tag:').pop())

            this._retrieveServicePlans(transmissionValue)
                .then((response) => {
                    if (response.length > 0) {
                        searchValue += `+tag:Transmission_Code-${this._planData.transmissionCode}`
                        this._searchForServicePlan(searchValue)
                    } else {
                        this._searchForServicePlan(searchValue)
                    }

                    return response
                })
                .catch((error) => {
                    throw new Error('Could not load data', error)
                })
        }
    }

    _refineSearchWithTags (response, tags) {
        let products = response.data.products
        let validProducts = []

        products.forEach((product) => {
            tags.forEach((tag) => {
                if (product.tags.includes(tag) && !validProducts.includes(product)) {
                    validProducts.push(product)
                }
            })
        })
        return validProducts
    }

    _searchForServicePlan (searchValue) {
        this._retrieveServicePlans(searchValue)
            .then((response) => {
                if (response.length > 0) {
                    if (window.theme.dev) {
                        window.console.log('Service plan search response:', response)
                    }
                    window.Frame.EventBus.emit('ServicePlan:hasPlan', true)
                    this._filterProducts(response)
                    return
                }
                window.Frame.EventBus.emit('ServicePlan:hasPlan', false)
                throw new Error('No products')
            })
            .catch((error) => {
                this._handleError(error.message || error)
            })
    }

    /**
     * Retrieve service plan products.
     * @param {String} searchValue - The search string.
     */
    _retrieveServicePlans (searchValue) {
        return new Promise((resolve, reject) => {
            axios.get(`/search?type=product&q=${searchValue}&view=sku`)
                .then((response) => {
                    if (response) {
                        resolve(response.data.products)
                        return
                    }

                    throw new Error('No response')
                })
                .catch((error) => {
                    reject(error)
                })
        })
    }
    /**
     * Filter products by mileage and production date based on form values.
     * Can return no products so receiving _must handle this.
     * @param {Array} products - The products in the response.
     */
    _filterProducts (products) {
        const date = this._calculateDate(this._planData.productionDate)

        // If options for a specified service plan don't exist - default to 'Plan_Type-ServicePlan'.
        if (this._nodeSelectors.wearAndTear === null &&
            this._nodeSelectors.servicePlan === null &&
            this._nodeSelectors.serviceBundle === null) {
            products = products.filter((product) => product.tags.includes('Plan_Type-ServicePlan'))
        }

        let filteredProducts = products.filter((product) => this._filterByProductionDate(product, date) !== false)

        if (this._annualMileage) {
            filteredProducts = filteredProducts.filter((product) => this._filterByMileage(product, this._planData.annualMileage) !== false)
        }

        filteredProducts = filteredProducts.filter((product) => this._filterByPlan(product, this._planData.planTag) !== false)

        if (window.theme.dev) {
            window.console.log('Service plan filtered products', filteredProducts)
        }

        if ((filteredProducts.length > 1 && this._serviceBundle === false) ||
            (filteredProducts.length > 2 && this._serviceBundle === true)
        ) {
            return this._handleError('Too many products', products)
        }

        if (filteredProducts.length === 0) {
            window.Frame.EventBus.emit('ServicePlan:hasPlan', false)
            return this._handleError('No products')
        }

        return this._renderResponse(filteredProducts)
    }

    /**
     * Render the filtered product's details.
     * @param {Object} product - The filtered product.
     */
    _renderResponse (product) {
        const singleProduct = product[0]

        /**
         * Render monthly cost.
         * - If no monthly cost tag then hide all monthly payment content.
         */
        let monthlyCost = this._retrieveTag(singleProduct.tags, 'Monthly_Cost')
        let schemeCodeTag = this._retrieveTag(singleProduct.tags, 'SC_')
        if (monthlyCost) {
            this._nodeSelectors.monthlyPayment.forEach((element) => {
                element.classList.remove(cssClasses.hidden)
            })

            monthlyCost = monthlyCost.replace('Monthly_Cost:', '').trim()
            const unformattedMonthlyCost = parseFloat(monthlyCost)
            monthlyCost = formatMoney(unformattedMonthlyCost * 100, window.theme.moneyFormat)

            this._nodeSelectors.monthlyCost.forEach((element) => {
                element.innerHTML = monthlyCost
                // displays 'per month' text for dealship once a price has rendered
                if (element.innerHTML.length > 1) {
                    element.parentElement.querySelector('.sp-form-step__price-label--monthly').classList.remove(cssClasses.hidden)
                }
            })

            this._updateData('monthlyCost', unformattedMonthlyCost)

            if (schemeCodeTag) {
                schemeCodeTag = schemeCodeTag.replace('SC_', '').trim()
                this._updateData('schemeCode', schemeCodeTag)
            }
        } else {
            this._nodeSelectors.monthlyPayment.forEach((element) => {
                element.classList.add(cssClasses.hidden)
            })
        }

        /**
         * Render total cost.
         */
        const totalCost = formatMoney(singleProduct.price, window.theme.moneyFormat)
        this._nodeSelectors.totalCost.forEach((element) => {
            element.innerHTML = totalCost
        })

        // this._nodeSelectors.status.classList.add(cssClasses.hidden)
        this._nodeSelectors.costs.classList.add(cssClasses.active)

        /**
         * Continue form.
         */
        this._nodeSelectors.container.setAttribute('data-form-step', 'step3')
        this._renderStep3(singleProduct)

        if (this._nodeSelectors.addToCart) {
            this._updateAddToCart(singleProduct)
        }

        return singleProduct
    }

    _handleDealerMonthlyCost () {
        const currency = this._getActiveCurrency()

        window.setTimeout(() => {
            const monthlyCost = Number(this._planData.monthlyCost)
            const monthlyCost2 = Number(this._planData.monthlyCost2)

            const commbinedMonthlyCost = (monthlyCost + monthlyCost2)

            const formatedCost = `${currency}${(commbinedMonthlyCost).toFixed(2).replace('.00', '')}`

            this._nodeSelectors.monthlyCost.forEach((element) => {
                element.innerHTML = formatedCost
                // displays 'per month' text for dealship once a price has rendered
                if (element.innerHTML.length > 1) {
                    element.parentElement.querySelector('.sp-form-step__price-label--monthly').classList.remove(cssClasses.hidden)
                }
            })
        }, 500)
    }

    _getActiveCurrency () {
        let currency = '£'
        const active = window.Shopify.currency.active

        if (active === 'EUR') { currency = '€' }

        return currency
    }

    /**
     * Utility functions.
     * ------------------------------------------------------------------------------
     * Helper functions to check statuses etc.
     *
     */

    /**
     * Checks if the annual mileage value is valid.
     * @returns {Boolean}
     */
    _isAnnualMileageValid () {
        if (!this._annualMileage) {
            return true
        }

        const regex = RegExp('^[0-9]*$')
        const annualMileageValue = Number(this._nodeSelectors.annualMileage.value)

        if (!regex.test(annualMileageValue)) {
            return false
        }

        const min = this._nodeSelectors.annualMileage.getAttribute('min')
        const max = this._nodeSelectors.annualMileage.getAttribute('max')
        const allowedMax = this._nodeSelectors.annualMileage.getAttribute('allowedMax')

        return (annualMileageValue >= min) && (annualMileageValue <= max) && (annualMileageValue <= allowedMax)
    }
    _isZipValid (element) {
        const value = element.value
        return /([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9][A-Za-z]?))))\s?[0-9][A-Za-z]{2})/.test(value)
    }

    /**
     * Checks if the form has been submimtted yet.
     * @returns {Boolean}
     */
    _isFormSubmitted () {
        const formSubmitted = this._nodeSelectors.container.getAttribute('data-form-submitted')

        if (formSubmitted === 'true') {
            return true
        }

        return false
    }

    /**
     * Checks if step 2 fields are all valid.
     * @returns {Boolean}
     */
    _isStep2Valid () {
        const invalidFields = this._nodeSelectors.field.filter((element) => {
            const key = element.getAttribute('data-input')
            if (key === 'zip') {
                return !this._isZipValid(element)
            } else {
                return (element.hasAttribute('required')) && (element.value === '')
            }
        })
        return invalidFields.length === 0
    }

    /**
     * Handle form errors.
     * @param {String} errorType - The type of error.
     * @param {Array} products - Products which caused the error.
     */
    _handleError (errorType, products) {
        switch (errorType) {
        case 'No products':
            this._elem.classList.add('no-plans')
            this._nodeSelectors.statusCopy.innerText = window.theme.strings.servicePlans.errors.noProducts
            break

        case 'Too many products':
            this._nodeSelectors.statusCopy.innerText = window.theme.strings.servicePlans.errors.lookup
            break

        case 'Invalid mileage':
            this._nodeSelectors.statusCopy.innerText = window.theme.strings.servicePlans.errors.incomplete
            break
        }

        this._nodeSelectors.status.classList.remove(cssClasses.loading)
        this._nodeSelectors.status.classList.add(cssClasses.active)

        if (products) {
            throw new Error(errorType, products)
        }

        throw new Error(errorType)
    }

    /**
     * Updates a fields validity status.
     * @param {HTMLElement} field - The field.
     * @param {Boolean} valid - Whether it's valid or not.
     */
    _updateFieldValidity (field, valid) {
        const parent = field.closest(this._settings.validity)

        if (valid) {
            parent.classList.add(cssClasses.valid)
            parent.classList.remove('is-invalid')
            return
        }

        parent.classList.remove(cssClasses.valid)

        if (field.value.length > 0) {
            parent.classList.add('is-invalid')
        }
    }

    /**
     * Filter products by the production date tag on each product.
     * @param {Object} product - The product to test.
     * @param {String} date - The production date.
     * @returns {Object}
     */
    _filterByProductionDate (product, date) {
        const matchValue = 'Build_Date'
        let tag = this._retrieveTag(product.tags, matchValue)

        // If it doesn't require a certain date, return
        if (tag === '') {
            return true
        }

        // Retrieve info from the tag
        tag = tag.split('--')
        tag = tag[1].split('_')

        // Set type (before/after) and required date
        const type = tag[0]
        let requiredDate = tag[1]
        requiredDate = this._calculateDate(requiredDate)

        if ((type === 'Before') && (date.getTime() < requiredDate.getTime())) {
            return true
        } else if ((type === 'After') && (date.getTime() >= requiredDate.getTime())) {
            return true
        }

        return false
    }

    /**
     * Filter products by the plan tag on each product.
     * @param {Object} product - The product to test.
     * @param {String} plan - The plan chosen in the form
     */
    _filterByPlan (product, plan) {
        const matchValue = 'Plan_Type'
        let tag = this._retrieveTag(product.tags, matchValue)

        if (tag === plan) {
            return true
        } else {
            return false
        }
    }

    /**
     * Filter products by the mileage tag on each product.
     * @param {Object} product - The product to test.
     * @param {String} mileage - The mileage entered in the form.
     */
    _filterByMileage (product, mileage) {
        const matchValue = 'Annual_Mileage'
        let tags = this._retrieveTag(product.tags, matchValue)

        if (tags.length > 1) {
            return this._checkMileageMeetsTagConditions(mileage, tags)
        } else {
            let tag = tags[0]

            // If it doesn't require a certain date, return
            if (tag === '') {
                return true
            }

            // Retrieve info from the tag
            tag = tag.split('--')
            tag = tag[1].split('_')
            // Set type (LT/GTE) and required mileage
            const type = tag[0]
            const requiredMileage = Number(tag[1])
            if ((type === 'LT') && (mileage < requiredMileage)) {
                return true
            } else if ((type === 'GTE') && (mileage >= requiredMileage)) {
                return true
            }
            return false
        }
    }

    /**
     * Return true if mileage meets Annual_Mileage tag conditions
     * @param {Number} mileage
     * @param {Array} tags
     */
    _checkMileageMeetsTagConditions (mileage, tags) {
        if (tags.length <= 1) return false

        let LT = 0
        let GTE = 0

        tags.forEach((tag) => {
            if (tag.includes('LT')) {
                tag = tag.split('_')
                LT = parseInt(tag[2])
            }

            if (tag.includes('GTE')) {
                tag = tag.split('_')
                GTE = parseInt(tag[2])
            }
        })

        if ((mileage >= GTE) && (mileage < LT)) {
            return true
        }

        return false
    }

    /**
     * Create a UNIX compatible date value.
     * @param {String} dateValue - The date string.
     * @returns {String}
     */
    _calculateDate (dateValue) {
        const date = dateValue.split('-')
        const year = Number(date[0])
        const month = (Number(date[1]) - 1)
        const day = (date[2]) ? Number(date[2]) : 1

        return new Date(year, month, day)
    }

    /**
     * Retrieve the required tag.
     * @param {Object} tags - The tags object.
     * @param {String} matchValue - The value to match.
     * @returns {String}
     */
    _retrieveTag (tags, matchValue) {
        let match = ''

        if (matchValue === 'Annual_Mileage') {
            match = []
        }

        tags.forEach((tag) => {
            if (tag.includes(matchValue)) {
                if (matchValue === 'Annual_Mileage') {
                    match.push(tag)
                } else {
                    match = tag
                }
            }
        })

        return match
    }
}

export default ServicePlansForm



// WEBPACK FOOTER //
// ./src/resources/scripts/components/ServicePlansForm.js